import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import { api } from '../api/client'
import type { BusinessAppSummary } from '../api/types'
import { BottoniEsporta } from '../components/BottoniEsporta'
import { CriticitaBadge, useAmministra } from '../components/BusinessApps'
import { Card, EmptyState, ErrorBanner, Spinner } from '../components/common'
import type { Column } from '../components/DataTable'
import { DataTable } from '../components/DataTable'
import { useAsync } from '../hooks/useAsync'
import { t } from '../i18n/lingua'
import { CRITICALITY_LABEL } from './Services'

/**
 * Business App: the applications as the business names them. The elements
 * are programs picked on the Mappa Software; the functions say what the app
 * does, each with its criticality; the business services using the app
 * inherit the highest one. See business_app_service.
 */
export default function BusinessApps() {
  const dati = useAsync(() => api.businessApps(), [])
  const admin = useAmministra()
  const navigate = useNavigate()
  const [nuova, setNuova] = useState(false)
  const [nome, setNome] = useState('')
  const [errore, setErrore] = useState<unknown>(null)

  async function crea() {
    setErrore(null)
    try {
      const app = await api.createBusinessApp({ name: nome.trim() })
      navigate(`/business-apps/${app.id}`)
    } catch (e) { setErrore(e) }
  }

  const colonne: Column<BusinessAppSummary>[] = [
    { key: 'name', header: t('Business App'), render: (a) => <Link to={`/business-apps/${a.id}`}>{a.name}</Link> },
    { key: 'criticality', header: t('Criticità'), value: (a) => (a.criticality ? CRITICALITY_LABEL[a.criticality] ?? a.criticality : ''),
      render: (a) => <CriticitaBadge value={a.criticality} /> },
    { key: 'functions', header: t('Funzionalità'), value: (a) => a.functions, filter: false },
    { key: 'elements', header: t('Elementi'), value: (a) => a.elements, filter: false },
    { key: 'cis', header: t('CI'), value: (a) => a.cis, filter: false },
    { key: 'services', header: t('Business service'), value: (a) => a.services.map((s) => s.name).join(', '),
      render: (a) => a.services.length === 0 ? <small className="text-muted">—</small>
        : a.services.map((s, i) => <span key={s.id}>{i > 0 && ', '}<Link to={`/services/${s.id}`}>{s.name}</Link></span>) },
    { key: 'owner', header: t('Responsabile'), value: (a) => a.owner ?? '' },
  ]

  return (
    <>
      <header className="page__header">
        <div>
          <h1 className="page__title">{t('Business App')}</h1>
          <p className="page__subtitle">
            {t('Rappresentazione delle applicazioni dal punto di vista dei servizi erogati. Sono identificati i componenti software, le funzionalità e le criticità. La criticità associata alle applicazioni corrisponde a quella più elevata e viene ereditata dai Business Service e da tutti i CI che le compongono.')}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <BottoniEsporta
            path={(fmt) => api.businessAppsExportPath(fmt)}
            nome="censo-business-app"
            disabled={!dati.data?.length}
          />
          {admin && <button onClick={() => setNuova((v) => !v)}>{nuova ? t('Annulla') : t('Nuova Business App')}</button>}
        </div>
      </header>
      {nuova && (
        <Card>
          <ErrorBanner error={errore} />
          <div className="toolbar" style={{ gap: 8 }}>
            <input value={nome} onChange={(e) => setNome(e.target.value)} placeholder={t('Nome della nuova Business App')} />
            <button disabled={!nome.trim()} onClick={crea}>{t('Crea')}</button>
          </div>
          <p className="field__hint">
            {t('Gli elementi si aggiungono dalla Mappa Software: si sceglie un CI, si cliccano i programmi e si aggiungono all’app.')}{' '}
            <Link to="/software-flow">{t('Apri la Mappa Software')}</Link>
          </p>
        </Card>
      )}
      <ErrorBanner error={dati.error} />
      <Card>
        {dati.loading && !dati.data ? <Spinner /> : (dati.data ?? []).length === 0 ? (
          <EmptyState title={t('Nessuna Business App')}
            hint={t('Si creano dalla Mappa Software scegliendo i programmi che compongono un’applicazione, oppure da qui con «Nuova Business App».')} />
        ) : (
          <DataTable rows={dati.data ?? []} rowKey={(a) => a.id} columns={colonne} initialSort={{ key: 'name' }}
            conteggio={{ nome: t('Business App') }} />
        )}
      </Card>
    </>
  )
}
